import Fuse from "fuse.js";

import { Field } from "../../models/mapLayer";

export const FIELD_SYNONYM_MAP: Record<string, string[]> = {
  Porosity: ["phi", "phie", "phit", "por", "poro", "porosity", "effective porosity"],
  Sw: ["sw", "swat", "water sat", "water saturation", "s w", "swir", "swi"],
  Temperature: [
    "temp",
    "t",
    "tres",
    "t res",
    "bh temp",
    "bht",
    "res temp",
    "temperature"
  ],
  Pressure: [
    "pres",
    "p",
    "p res",
    "res pres",
    "reservoir pressure",
    "bh pressure",
    "bhp",
    "fbhp",
    "pressure"
  ],
  "Depth SS": ["depth ss", "ss", "depthss", "depth sub sea"],
  "Depth TVD": ["depth tvd", "tvd", "depthtvd", "sstvd", "ss tvd", "tvd ss"],
  "Net Pay": ["net pay", "netpay", "np"],
  "Gross Pay": ["gross pay", "grosspay", "gp"],
  Gradient: ["gradient", "grad"],
  "Pressure Gradient": ["pressure gradient", "pressuregradient", "pres grad"],
  "Specific Gravity": ["specific gravity", "specificgravity", "specgrav", "sg"]
};

// Derived flat lookup: normalized alias (no separators, lowercase) → canonical field name.
export const FIELD_ALIASES: Record<string, string> = Object.entries(
  FIELD_SYNONYM_MAP
).reduce<Record<string, string>>((acc, [canonical, aliases]) => {
  for (const alias of aliases) {
    acc[alias.toLowerCase().replace(/[-_\s]+/g, "")] = canonical;
  }
  return acc;
}, {});

export function normalizeForMatching(str: string): string {
  return str
    .toLowerCase()
    .replace(/\.[^.]+$/, "")
    .replace(/[-_.]+/g, " ")
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function extractTokens(normalized: string): string[] {
  return normalized.split(" ").filter((t) => t.length > 0);
}

export function findAliasMatch(fileName: string): string | undefined {
  const normalized = normalizeForMatching(fileName);
  const tokens = extractTokens(normalized);

  const noSpaces = normalized.replace(/\s+/g, "");
  if (FIELD_ALIASES[noSpaces]) return FIELD_ALIASES[noSpaces];

  for (const token of tokens) {
    if (FIELD_ALIASES[token]) return FIELD_ALIASES[token];
  }

  // Check consecutive n-grams: ["net", "pay", "grid"] → "netpay", "paygrid", etc.
  for (let size = 2; size <= tokens.length; size++) {
    for (let i = 0; i <= tokens.length - size; i++) {
      const ngram = tokens.slice(i, i + size).join("");
      if (FIELD_ALIASES[ngram]) return FIELD_ALIASES[ngram];
    }
  }

  return undefined;
}

export function findTokenMatch(fileName: string, fieldTypes: Field[]): Field | undefined {
  const fileTokens = extractTokens(normalizeForMatching(fileName));

  for (const token of fileTokens) {
    const match = fieldTypes.find((f) => normalizeForMatching(f.name) === token);
    if (match) return match;
  }

  return undefined;
}

export function findFuzzyMatch(fileName: string, fieldTypes: Field[]): Field | undefined {
  if (fieldTypes.length === 0) return undefined;

  const normalizedFields = fieldTypes.map((f) => ({
    field: f,
    normalized: normalizeForMatching(f.name)
  }));

  const fuse = new Fuse(normalizedFields, {
    keys: ["normalized"],
    threshold: 0.4,
    includeScore: true
  });

  const results = fuse.search(normalizeForMatching(fileName));
  return results.length > 0 ? results[0].item.field : undefined;
}

/**
 * Matches a filename to the best field using, in priority order:
 *  1. Alias map — known industry synonyms (phi → Porosity, bhp → Pressure, etc.)
 *  2. Token match — any filename token exactly matches a field name
 *  3. Fuse.js fuzzy — scored fuzzy search as final fallback
 */
export function findBestFieldMatch(
  fileName: string,
  fieldTypes: Field[]
): Field | undefined {
  if (fieldTypes.length === 0) return undefined;

  const aliasCanonical = findAliasMatch(fileName);
  if (aliasCanonical) {
    const normalizedCanonical = normalizeForMatching(aliasCanonical);
    const field = fieldTypes.find(
      (f) => normalizeForMatching(f.name) === normalizedCanonical
    );
    if (field) return field;
  }

  const tokenMatch = findTokenMatch(fileName, fieldTypes);
  if (tokenMatch) return tokenMatch;

  return findFuzzyMatch(fileName, fieldTypes);
}
